export const sortExpressions = {
  byOrder: <T extends { order: number }>(a: T, b: T) => a.order - b.order,
  byEstimationVersionNumberDesc: <T extends { estimation: { versionNumber: number } }>(
    a: T,
    b: T,
  ) => b.estimation.versionNumber - a.estimation.versionNumber,
  byCreatedAt: <T extends { createdAt: string }>(a: T, b: T) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
};

/**
 * Generic function to sort items (resource types or cost types) respecting the order of their groups.
 * Items from groups are sorted by group order first, then by item order within the group.
 * Standalone items come after all groups, sorted by their order.
 */
function sortGroupedItems<T extends { order: number }, G extends { order: number; items: T[] }>(
  standaloneItems: T[] | undefined | null,
  groups: G[] | undefined | null,
): T[] {
  const result: T[] = [];

  // First, add items from groups (sorted by group order, then by item order within each group)
  if (groups && groups.length > 0) {
    const sortedGroups = [...groups].sort(sortExpressions.byOrder);
    for (const group of sortedGroups) {
      const sortedItems = [...group.items].sort(sortExpressions.byOrder);
      result.push(...sortedItems);
    }
  }

  // Then, add standalone items (sorted by their order)
  if (standaloneItems && standaloneItems.length > 0) {
    const sortedStandalone = [...standaloneItems].sort(sortExpressions.byOrder);
    result.push(...sortedStandalone);
  }

  return result;
}

/**
 * Sorts resource types respecting the order of resource type groups.
 * Resource types from groups are sorted by group order first, then by resource type order within the group.
 * Standalone resource types come after all groups, sorted by their order.
 */
export function sortResourceTypes<T extends { order: number }>(
  resourceTypes: T[] | undefined | null,
  resourceTypeGroups: Array<{ order: number; resourceTypes: T[] }> | undefined | null,
): T[] {
  return sortGroupedItems(
    resourceTypes,
    resourceTypeGroups?.map((group) => ({ ...group, items: group.resourceTypes })),
  );
}

/**
 * Sorts cost types respecting the order of cost type groups.
 * Cost types from groups are sorted by group order first, then by cost type order within the group.
 * Standalone cost types come after all groups, sorted by their order.
 */
export function sortCostTypes<T extends { order: number }>(
  costTypes: T[] | undefined | null,
  costTypeGroups: Array<{ order: number; costTypes: T[] }> | undefined | null,
): T[] {
  return sortGroupedItems(
    costTypes,
    costTypeGroups?.map((group) => ({ ...group, items: group.costTypes })),
  );
}
